import { Fragment } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { GlobalMigrateUserModal } from '../components/GlobalMigrateUserModal/GlobalMigrateUserModal';
import AuthorizedGuard from '../guards/AuthorizedGuard';
import CmsGuard from '../guards/CmsGuard';
import LazyGuard from '../guards/LazyGuard';
import { LoanDocumentGuard } from '../guards/LoanDocumentGuard';
import RequireAuth from '../guards/RequireAuth';
import RequireContext from '../guards/RequireContext';
import SessionGuard from '../guards/SessionGuard';
import StateGuard from '../guards/StateGuard';
import UIRevampCleanupGuard from '../guards/UIRevampCleanupGuard/UIRevampCleanupGuard';
import SetupParamsGuard from '../guards/UrlParams/SetupParamsGuard';
import { UrlParamsProcessors } from '../guards/UrlParams/UrlParamsProcessors';
import WorkflowGuard from '../guards/WorkflowGuard';
import AccountDetails from '../pages/Account/Account';
import ActivationPage from '../pages/Activation';
import { ApplicationDetails } from '../pages/Applications/ApplicationDetails';
import ApplicationUpdatePage from '../pages/ApplicationUpdate';
import DashboardView from '../pages/Dashboard';
import LoanDocumentsList from '../pages/LoanDocuments/LoanDocumentsList';
import LoanDocumentsSubmitted from '../pages/LoanDocuments/LoanDocumentsSubmitted';
import LoanDocumentsUpload from '../pages/LoanDocuments/LoanDocumentsUpload';
import { MainLayout } from '../pages/MainLayout/MainLayout';
import RateUpdatePage from '../pages/RateUpdate';
import SignInView from '../pages/SignIn';
import SignInVerifyPinView from '../pages/SignInVerifyPin';
import SignOutView from '../pages/SignOut';
import { VehicleRoot } from '../pages/Vehicle/VehicleRoot';
import useApplicantStore from '../store/applicantStore/useApplicantStore';
import useDashboardStore from '../store/dashboardStore/useDashboardStore';
import useVerificationStore from '../store/verificationStore/useVerificationStore';
import { VERIFICATION_GATE } from '../workflow/assembly/DefaultVerification/gate';
import { APPLICANT_GATE } from '../workflow/assembly/UnifiedDefaultApplicant/gate';
import { CO_APPLICANT_GATE } from '../workflow/assembly/UnifiedDefaultCoApplicant/gate';
import { NO_OFFERS_GATE } from '../workflow/assembly/UnifiedDefaultNoOffers/gate';
import { OFFERS_GATE } from '../workflow/assembly/UnifiedDefaultOffers/gate';
import { REVIEW_GATE } from '../workflow/assembly/UnifiedDefaultReview/gate';
import { SIGN_UP_GATE } from '../workflow/assembly/UnifiedDefaultSignUp/gate';
import { GateRoot } from '../workflow/common/Gate/Gate';
import { WorkflowHub } from '../workflow/common/WorkflowHub';
import { NavigateWithState } from './NavigateWithState';

const unifiedInternalRoutes = [
  {
    path: 'sign-up/*',
    element: (
      <AuthorizedGuard>
        <WorkflowHub />
      </AuthorizedGuard>
    ),
    children: [
      {
        path: '*',
        index: true,
        element: <GateRoot gate={SIGN_UP_GATE} />,
      },
    ],
  },
  {
    path: 'sign-in',
    element: (
      <AuthorizedGuard>
        <SignInView />
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-in/activation',
    element: (
      <AuthorizedGuard>
        <ActivationPage />
      </AuthorizedGuard>
    ),
  },

  {
    path: 'sign-in/rate-update',
    element: (
      <AuthorizedGuard>
        <RateUpdatePage />
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-in/application-update',
    element: (
      <AuthorizedGuard>
        <ApplicationUpdatePage />
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-in/verify/pin',
    element: (
      <AuthorizedGuard>
        <StateGuard>
          <SignInVerifyPinView />
        </StateGuard>
      </AuthorizedGuard>
    ),
  },
  {
    path: 'sign-out',
    element: <SignOutView />,
  },
  {
    path: 'loan-documents',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <RequireContext>
            <Outlet />
          </RequireContext>
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="list" />,
      },
      {
        path: 'list',
        element: <LoanDocumentsList />,
      },
      {
        path: 'retail-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'service-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'gap-contract',
        element: <LoanDocumentsUpload />,
      },
      {
        path: 'submitted',
        element: <LoanDocumentsSubmitted />,
      },
    ],
  },
  {
    path: 'workflow',
    element: (
      <RequireAuth>
        <WorkflowHub />
      </RequireAuth>
    ),
    children: [
      {
        path: 'applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'co-applicant/*',
        element: (
          <RequireContext>
            <GateRoot gate={CO_APPLICANT_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={OFFERS_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'review/*',
        element: (
          <RequireContext>
            <GateRoot gate={REVIEW_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'verification/*',
        element: (
          <RequireContext>
            <GateRoot gate={VERIFICATION_GATE} />
          </RequireContext>
        ),
      },
      {
        path: 'no-offers/*',
        element: (
          <RequireContext>
            <GateRoot gate={NO_OFFERS_GATE} />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <RequireAuth>
        <UrlParamsProcessors>
          <MainLayout />
        </UrlParamsProcessors>
      </RequireAuth>
    ),
    children: [
      {
        path: '',
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: 'dashboard/*',
        children: [
          {
            index: true,
            element: <DashboardView />,
          },
          {
            path: 'vehicle/*',
            element: (
              <RequireContext>
                <StateGuard>
                  <VehicleRoot />
                </StateGuard>
              </RequireContext>
            ),
            children: [
              {
                path: '*',
                index: true,
                element: <NavigateWithState to="application" replace />,
              },
              {
                path: 'application/*',
                element: <ApplicationDetails />,
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to="" replace />,
          },
        ],
      },
      {
        path: 'details/*',
        element: (
          <RequireContext>
            <AccountDetails />
          </RequireContext>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="" replace />,
  },
];

export const routes = [
  {
    path: '',
    element: (
      <SetupParamsGuard>
        <Fragment>
          <Outlet />
          <GlobalMigrateUserModal />
        </Fragment>
      </SetupParamsGuard>
    ),
    children: [
      {
        path: 'sign-in',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/SignInPage/SignInPage'),
          },
          {
            path: 'application-update',
            lazy: () =>
              import('../pages/ApplicationUpdatePage/ApplicationUpdatePage'),
          },
          {
            path: 'pin-verify',
            lazy: () =>
              import('../pages/SignInPinVerifyPage/SignInPinVerifyPage'),
          },
        ],
      },
      {
        path: 'sign-up',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/SignUpPage/SignUpPage'),
          },
          {
            path: 'pin-verify',
            lazy: () =>
              import('../pages/SignUpPinVerifyPage/SignUpPinVerifyPage'),
          },
          {
            path: 'returning-user',
            lazy: () =>
              import(
                '../pages/SignUpReturningUserPage/SignUpReturningUserPage'
              ),
          },
        ],
      },
      {
        path: 'sign-out',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <LazyGuard>
              <Outlet />
            </LazyGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/SignOutPage/SignOutPage'),
          },
        ],
      },
      {
        path: 'activation',
        isPublicRoute: true,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/ActivationPage/ActivationPage'),
          },
          {
            path: 'pin-verify',
            lazy: () =>
              import(
                '../pages/ActivationPinVerifyPage/ActivationPinVerifyPage'
              ),
          },
        ],
      },
      {
        path: 'dashboard',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/garage" />,
          },
          {
            path: 'garage',
            lazy: () =>
              import('../pages/DashboardGaragePage/DashboardGaragePage'),
          },
          {
            path: 'application/:vehicleId',
            lazy: () =>
              import(
                '../pages/DashboardApplicationPage/DashboardApplicationPage'
              ),
          },
        ],
      },
      {
        path: 'update-vehicle/:vehicleId',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () =>
              import(
                '../pages/VehicleConfirmationPage/VehicleConfirmationPage'
              ),
          },
        ],
      },
      {
        path: 'apply',
        element: (
          <WorkflowGuard>
            <CmsGuard>
              <SessionGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </SessionGuard>
            </CmsGuard>
          </WorkflowGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/Apply/ApplyPage/ApplyPage'),
          },
          {
            path: 'application-type',
            lazy: () =>
              import(
                '../pages/Apply/ApplyApplicationTypePage/ApplyApplicationTypePage'
              ),
          },
          {
            path: 'refinance-goal',
            lazy: () =>
              import(
                '../pages/Apply/ApplyRefinanceGoalPage/ApplyRefinanceGoalPage'
              ),
          },
          {
            path: 'purchase-type',
            lazy: () =>
              import(
                '../pages/Apply/ApplyPurchaseTypePage/ApplyPurchaseTypePage'
              ),
          },
          {
            path: 'vehicle-condition',
            lazy: () =>
              import(
                '../pages/Apply/ApplyVehicleConditionPage/ApplyVehicleConditionPage'
              ),
          },
          {
            path: 'search-vehicle',
            lazy: () =>
              import(
                '../pages/Apply/ApplySearchVehiclePage/ApplySearchVehiclePage'
              ),
          },
          {
            path: 'search-vin',
            lazy: () =>
              import('../pages/Apply/ApplySearchVinPage/ApplySearchVinPage'),
          },
          {
            path: 'search-plate',
            lazy: () =>
              import(
                '../pages/Apply/ApplySearchPlatePage/ApplySearchPlatePage'
              ),
          },
          {
            path: 'search-year',
            lazy: () =>
              import('../pages/Apply/ApplySearchYearPage/ApplySearchYearPage'),
          },
          {
            path: 'search-make',
            lazy: () =>
              import('../pages/Apply/ApplySearchMakePage/ApplySearchMakePage'),
          },
          {
            path: 'search-model',
            lazy: () =>
              import(
                '../pages/Apply/ApplySearchModelPage/ApplySearchModelPage'
              ),
          },
          {
            path: 'confirm-vehicle',
            lazy: () =>
              import(
                '../pages/Apply/ApplyConfirmVehiclePage/ApplyConfirmVehiclePage'
              ),
          },
        ],
      },
      {
        path: 'applicant/:vehicleId',
        loader: ({ params }) => useApplicantStore.getState().loader(params),
        shouldRevalidate: ({ currentUrl, nextUrl }) =>
          useDashboardStore.getState().computed.isVehicleEmpty ||
          currentUrl.pathname !== nextUrl.pathname,
        element: (
          <WorkflowGuard>
            <CmsGuard>
              <SessionGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </SessionGuard>
            </CmsGuard>
          </WorkflowGuard>
        ),
        children: [
          {
            index: true,
            lazy: () =>
              import('../pages/Applicant/ApplicantPage/ApplicantPage'),
          },
          {
            path: 'residence-type',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantResidenceTypePage/ApplicantResidenceTypePage'
              ),
          },
          {
            path: 'residence-details',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantResidenceDetailsPage/ApplicantResidenceDetailsPage'
              ),
          },
          {
            path: 'residence-duration',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantResidenceDurationPage/ApplicantResidenceDurationPage'
              ),
          },
          {
            path: 'previous-residence',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantPreviousResidencePage/ApplicantPreviousResidencePage'
              ),
          },
          {
            path: 'employment-status',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEmploymentStatusPage/ApplicantEmploymentStatusPage'
              ),
          },
          {
            path: 'employment-type',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEmploymentTypePage/ApplicantEmploymentTypePage'
              ),
          },
          {
            path: 'unemployment-type',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantUnemploymentTypePage/ApplicantUnemploymentTypePage'
              ),
          },
          {
            path: 'employment-details',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEmploymentDetailsPage/ApplicantEmploymentDetailsPage'
              ),
          },
          {
            path: 'unemployment-details',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantUnemploymentDetailsPage/ApplicantUnemploymentDetailsPage'
              ),
          },
          {
            path: 'employment-duration',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEmploymentDurationPage/ApplicantEmploymentDurationPage'
              ),
          },
          {
            path: 'previous-employment',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantPreviousEmploymentPage/ApplicantPreviousEmploymentPage'
              ),
          },
          {
            path: 'additional-income',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantAdditionalIncomePage/ApplicantAdditionalIncomePage'
              ),
          },
          {
            path: 'income-details',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantIncomeDetailsPage/ApplicantIncomeDetailsPage'
              ),
          },
          {
            path: 'add-coapplicant',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantAddCoapplicantPage/ApplicantAddCoapplicantPage'
              ),
          },
          {
            path: 'coapp-personal-relationship',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantRelationshipPage/CoapplicantRelationshipPage'
              ),
          },
          {
            path: 'coapp-personal-details',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantPersonalDetailsPage/CoapplicantPersonalDetailsPage'
              ),
          },
          {
            path: 'coapp-residence-relationship',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantResidenceRelationshipPage/CoapplicantResidenceRelationshipPage'
              ),
          },
          {
            path: 'coapp-same-residence',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantSameResidencePage/CoapplicantSameResidencePage'
              ),
          },
          {
            path: 'coapp-different-residence-type',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantDifferentResidenceTypePage/CoapplicantDifferentResidenceTypePage'
              ),
          },
          {
            path: 'coapp-different-residence',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantDifferentResidencePage/CoapplicantDifferentResidencePage'
              ),
          },
          {
            path: 'coapp-residence-duration',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantResidenceDurationPage/CoapplicantResidenceDurationPage'
              ),
          },
          {
            path: 'coapp-previous-residence',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantPreviousResidencePage/CoapplicantPreviousResidencePage'
              ),
          },
          {
            path: 'coapp-employment-status',
            loader: () => useApplicantStore.getState().loadApplicant(),
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentStatusPage/CoapplicantEmploymentStatusPage'
              ),
          },
          {
            path: 'coapp-employment-type',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentTypePage/CoapplicantEmploymentTypePage'
              ),
          },
          {
            path: 'coapp-unemployment-type',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantUnemploymentTypePage/CoapplicantUnemploymentTypePage'
              ),
          },
          {
            path: 'coapp-employment-details',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentDetailsPage/CoapplicantEmploymentDetailsPage'
              ),
          },
          {
            path: 'coapp-unemployment-details',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantUnemploymentDetailsPage/CoapplicantUnemploymentDetailsPage'
              ),
          },
          {
            path: 'coapp-employment-duration',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEmploymentDurationPage/CoapplicantEmploymentDurationPage'
              ),
          },
          {
            path: 'coapp-previous-employment',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantPreviousEmploymentPage/CoapplicantPreviousEmploymentPage'
              ),
          },
          {
            path: 'coapp-additional-income',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantAdditionalIncomePage/CoapplicantAdditionalIncomePage'
              ),
          },
          {
            path: 'coapp-income-details',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantIncomeDetailsPage/CoapplicantIncomeDetailsPage'
              ),
          },
          {
            path: 'edit-personal',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEditPersonalPage/ApplicantEditPersonalPage'
              ),
          },
          {
            path: 'coapp-edit-personal',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEditPersonalPage/CoapplicantEditPersonalPage'
              ),
          },
          {
            path: 'edit-residence',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEditResidencePage/ApplicantEditResidencePage'
              ),
          },
          {
            path: 'coapp-edit-residence',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEditResidencePage/CoapplicantEditResidencePage'
              ),
          },
          {
            path: 'edit-employment',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEditEmploymentPage/ApplicantEditEmploymentPage'
              ),
          },
          {
            path: 'coapp-edit-employment',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEditEmploymentPage/CoapplicantEditEmploymentPage'
              ),
          },
          {
            path: 'edit-income',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantEditIncomePage/ApplicantEditIncomePage'
              ),
          },
          {
            path: 'coapp-edit-income',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantEditIncomePage/CoapplicantEditIncomePage'
              ),
          },
          {
            path: 'review-applicant',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantReviewPage/ApplicantReviewPage'
              ),
          },
          {
            path: 'coapp-review-applicant',
            lazy: () =>
              import(
                '../pages/Coapplicant/CoapplicantReviewPage/CoapplicantReviewPage'
              ),
          },
          {
            path: 'submit-application',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantSubmitPage/ApplicantSubmitPage'
              ),
          },
          {
            path: 'application-success',
            lazy: () =>
              import(
                '../pages/Applicant/ApplicantSuccessPage/ApplicantSuccessPage'
              ),
          },
        ],
      },
      {
        path: 'loan-documents',
        element: (
          <CmsGuard>
            <SessionGuard>
              <LoanDocumentGuard>
                <LazyGuard>
                  <Outlet />
                </LazyGuard>
              </LoanDocumentGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () =>
              import('../pages/LoanDocument/LoanDocumentPage/LoanDocumentPage'),
          },
          {
            path: 'list',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentListPage/LoanDocumentListPage'
              ),
          },
          {
            path: 'ric-upload',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadRICPage/LoanDocumentUploadRICPage'
              ),
          },
          {
            path: 'vsc-upload',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadVSCPage/LoanDocumentUploadVSCPage'
              ),
          },
          {
            path: 'gap-upload',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadGAPPage/LoanDocumentUploadGAPPage'
              ),
          },
          {
            path: 'document-upload',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentUploadAllPage/LoanDocumentUploadAllPage'
              ),
          },
          {
            path: 'current-mileage',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentMileagePage/LoanDocumentMileagePage'
              ),
          },
          {
            path: 'success',
            lazy: () =>
              import(
                '../pages/LoanDocument/LoanDocumentSuccessPage/LoanDocumentSuccessPage'
              ),
          },
        ],
      },
      {
        path: 'verifications/:vehicleId',
        loader: ({ params, request }) =>
          useVerificationStore.getState().loader(params, request),
        shouldRevalidate: ({ currentUrl, nextUrl }) =>
          useDashboardStore.getState().computed.isVehicleEmpty ||
          currentUrl.pathname !== nextUrl.pathname,
        element: (
          <CmsGuard>
            <SessionGuard>
              <LazyGuard>
                <Outlet />
              </LazyGuard>
            </SessionGuard>
          </CmsGuard>
        ),
        children: [
          {
            index: true,
            lazy: () =>
              import(
                '../pages/Verifications/VerificationPage/VerificationPage'
              ),
          },
          {
            path: 'document-list',
            lazy: () =>
              import(
                '../pages/Verifications/VerificationDocumentListPage/VerificationDocumentListPage'
              ),
          },
          ...[
            'upload-license',
            'coapp-upload-license',
            'upload-insurance',
            'upload-registration',
            'upload-odometer',
            'upload-income',
            'coapp-upload-income',
            'upload-additional',
          ].map((path) => ({
            path: path,
            lazy: () =>
              import(
                '../pages/Verifications/VerificationUploadPage/VerificationUploadPage'
              ),
          })),
        ],
      },
      {
        path: 'not-found',
        element: (
          <LazyGuard>
            <Outlet />
          </LazyGuard>
        ),
        children: [
          {
            index: true,
            lazy: () => import('../pages/NotFoundPage/NotFoundPage'),
          },
        ],
      },
      {
        path: 'unified',
        element: (
          <UIRevampCleanupGuard>
            <Outlet />
          </UIRevampCleanupGuard>
        ),
        children: unifiedInternalRoutes,
      },
      {
        path: '',
        element: <Navigate to="unified" replace />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];
